import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

const SOCKET_URL = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');

export const SocketProvider = ({ children }) => {
    const { user } = useAuth();
    const [socket, setSocket] = useState(null);
    const [connected, setConnected] = useState(false);
    const socketRef = useRef(null);
    const mountedRef = useRef(false);

    useEffect(() => {
        if (!user?.id) return;

        const token = localStorage.getItem('token');
        if (!token) return;

        // Strict Mode: si ya hay un socket conectado, reusar
        if (socketRef.current?.connected) {
            setSocket(socketRef.current);
            setConnected(true);
            return;
        }

        // Limpiar socket viejo si existe (desconectado)
        if (socketRef.current) {
            socketRef.current.disconnect();
        }

        const newSocket = io(SOCKET_URL, {
            query: { token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            // Tras un deploy todos se desconectan a la vez: el azar reparte la primera
            // reconexión entre 0 y 10 s para no tumbar el backend con la avalancha
            reconnectionDelay: 5000,
            reconnectionDelayMax: 30000,
            randomizationFactor: 1,
            reconnectionAttempts: Infinity
        });

        newSocket.on('connect', () => {
            console.log('[Socket] Conectado. ID:', newSocket.id);
            setConnected(true);
        });

        newSocket.on('disconnect', (reason) => {
            console.log('[Socket] Desconectado. Razon:', reason);
            setConnected(false);
        });

        newSocket.on('connect_error', (err) => {
            console.error('[Socket] Error de conexion:', err.message);
        });

        newSocket.on('connected', (data) => {
            console.log('[Socket] Backend confirmo conexion. user_id:', data?.user_id);
        });

        newSocket.on('error', (error) => {
            console.error('[Socket] Error del servidor:', error?.message || error);
        });

        socketRef.current = newSocket;
        setSocket(newSocket);

        return () => {
            // NO desconectar inmediatamente — Strict Mode remontará enseguida
            // Solo marcar que se desmontó; el socket se limpia en el siguiente mount
            // o cuando el componente se desmonte definitivamente
            setTimeout(() => {
                if (!mountedRef.current && socketRef.current === newSocket) {
                    newSocket.disconnect();
                    socketRef.current = null;
                }
            }, 100);
        };
    }, [user?.id]);

    useEffect(() => {
        if (!user?.id) return;

        const ensureSocketConnection = () => {
            const currentSocket = socketRef.current;
            const token = localStorage.getItem('token');

            if (!currentSocket || !token || currentSocket.connected) {
                return;
            }

            currentSocket.io.opts.query = { token };
            currentSocket.connect();
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                ensureSocketConnection();
            }
        };

        window.addEventListener('focus', ensureSocketConnection);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', ensureSocketConnection);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user?.id]);

    // Track mount state for cleanup
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const value = { socket, connected };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};

export const useSocket = () => {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useSocket debe ser usado dentro de un SocketProvider');
    }
    return context;
};
